import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import { motion } from "framer-motion";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import UploadDoc from "../NewApplication/UploadDoc";
import Loader from "../../components/Loader";
import { FiBookOpen, FiUpload, FiInfo } from "react-icons/fi";

const QuickApplyDocs = ({ quickApplySessionId, onCompleteChange, modeOfEntry, onModeOfEntryChange }) => {
  // Fetch all payments and find the session payment
  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ["generatedRRR"],
    queryFn: async () => {
      const req = await axiosget("ms_application_fee");
      return req?.data;
    },
  });

  const sessionPayment = payments && payments?.type === undefined
    ? payments.find((v) => v?.trxdata?.quickApplySessionId === quickApplySessionId)
    : null;

  const programmeId = sessionPayment?.programme?._id;

  // Fetch admission requirements for the programme
  const { data: requirements, isLoading: reqLoading, error: reqError } = useQuery({
    queryKey: ["admissionRequirementforCourse", programmeId],
    queryFn: async () => {
      const req = await axiosget(`ms_programme_admission_requirement/${programmeId}`);
      return req?.data;
    },
    enabled: !!programmeId,
  });

  // Notify parent when all docs are uploaded
  useEffect(() => {
    if (requirements && requirements?.continue === 0 && modeOfEntry) {
      onCompleteChange(true);
    } else {
      onCompleteChange(false);
    }
  }, [requirements?.continue, modeOfEntry, onCompleteChange]);

  if (paymentsLoading) return <Loader />;

  if (!sessionPayment) {
    return (
      <Alert variant="warning">
        <AlertDescription>
          No payment found for this session. Please go back to the Payment step and generate a payment first.
        </AlertDescription>
      </Alert>
    );
  }

  if (!sessionPayment?.isverified) {
    return (
      <Alert variant="warning">
        <AlertDescription>
          Your payment has not been verified yet. Please go back to the Payment step and verify or apply a waiver first.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Programme Info */}
      <div className="bg-white rounded-xl border border-neutral-200 p-5">
        <p className="text-sm text-neutral-500">Programme</p>
        <p className="text-base font-semibold text-neutral-900">
          {sessionPayment?.programme?.code} - {sessionPayment?.programme?.name}
        </p>
      </div>

      {/* Mode of Entry Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
          <FiBookOpen className="h-4 w-4 text-primary-600" />
          <span>Select Mode of Entry</span>
        </div>

        <div className="relative">
          <select
            name="examination"
            value={modeOfEntry}
            onChange={(e) => onModeOfEntryChange(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-white text-neutral-900"
          >
            <option value="">Select Option</option>
            <option value="JAMB UTME">JAMB UTME</option>
            <option value="Transfer">Transfer</option>
            <option value="Direct Entry">Direct Entry (DE)</option>
          </select>
        </div>
      </div>

      {/* Document Upload Section */}
      {reqLoading && <FormSkeleton fields={3} />}

      {reqError && (
        <Alert variant="danger">
          <AlertDescription>
            Error loading requirements: {reqError?.message || reqError}
          </AlertDescription>
        </Alert>
      )}

      {requirements?.type !== undefined && (
        <Alert variant="danger">
          <AlertDescription>{requirements?.message}</AlertDescription>
        </Alert>
      )}

      {requirements && requirements?.type === undefined && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Info Alert */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FiInfo className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  Document Upload Requirements
                </h4>
                <p className="text-sm text-blue-700">
                  Please upload all required documents. Documents marked as optional can be skipped.
                </p>
              </div>
            </div>
          </div>

          {/* Upload Documents Grid */}
          {requirements?.list && requirements.list.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
                <FiUpload className="h-4 w-4 text-primary-600" />
                <span>Required Documents</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requirements.list.map((r, i) => (
                  <UploadDoc r={r} key={`expected_upload_${i}`} />
                ))}
              </div>
            </div>
          )}

          {/* Completion Status */}
          {requirements?.continue === 0 && modeOfEntry ? (
            <Alert variant="success">
              <AlertDescription>
                All documents uploaded and mode of entry selected. You can proceed to submit.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="info">
              <AlertDescription>
                {!modeOfEntry && "Please select a mode of entry. "}
                {requirements?.continue > 0 && `${requirements.continue} required document(s) remaining.`}
              </AlertDescription>
            </Alert>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default QuickApplyDocs;
